import { ProgressMode } from "@/shared/types/types";
import { Marker } from '@/shared/types/zod';
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { useStore } from "./Store";
import { pixelDistance } from "@/utils/MapUtils";
import { LngLatLike } from "maplibre-gl";
import { gsap } from "gsap/gsap-core";
import { type LineProjection } from "@/utils/lineSampling";

const HOVER_HIT_RADIUS_PX = 30;
const HOVER_TIE_TOLERANCE_PX = 3;

type HoverMatch = {
	match: LineProjection;
	distance: number;
}

export const useProgressStore = defineStore("progress", () => {

	const store = useStore();

	const progress = ref(1)
	const progressMode = ref<ProgressMode>("");
	const progressFeature = ref<Marker | undefined>(undefined);

	//Needed for gsap animation
	const animationProgress = computed({
		get(): number {
			return progress.value;
		},
		set(value: number) {
			setProgress(value, store.videoMode ? "animationVideo" : "animation");
		}
	});


	function setProgress(newProgress: number, newProgressMode: ProgressMode, markerId: string | undefined = undefined) {
		if (store._editMode) {
			progressMode.value = "";
			progress.value = 1;
			return;
		}

		if (progressMode.value == "animation") {
			if (newProgressMode == "track"
				|| newProgressMode == "marker"
				|| newProgressMode == "height") {
				stopAnimation();
			} else if (newProgressMode == "map") {
				return;
			}
		}

		if (markerId) {
			if (!progressFeature.value || progressFeature.value.id != markerId) {
				progressFeature.value = store.computedMarkers.find(m => m.id == markerId);
			}
		} else {
			progressFeature.value = undefined; // Clear feature if no markerId
		}


		progressMode.value = newProgressMode || "";
		progress.value = newProgress;
	}


	function animate(isTest: boolean) {
		stopAnimation();

		if (!store.map?.animate || isTest)
			return;

		setProgress(0, store.videoMode ? "animationVideo" : "animation");
		const isVideo = store.videoMode;

		let duration = Math.min(store.actualTrackLength * 0.12 + 3.5, 7);
		if (isVideo) duration = 20;
		gsap.fromTo(animationProgress,
			{
				value: 0,
			},
			{
				value: 1,
				duration: duration,
				delay: isVideo ? 5 : .6,
				ease: isVideo ? "linear" : "sine.inOut",
				onComplete: () => {
					setProgress(1, "");
				}
			});
	}

	function stopAnimation() {
		gsap.killTweensOf(animationProgress);
		setProgress(1, "");
	}

	const mouseState = {
		mouseCoords: undefined as [number, number] | undefined,
		featureId: undefined as string | undefined,
		featureProgresses: [] as number[],
	}

	function handleMouseMoveFeature(id: string, progresses: number[]) {
		mouseState.featureId = id;
		mouseState.featureProgresses = progresses;
		requestUpdate()
	}

	function handleMouseMoveMap(coords: [number, number]) {
		mouseState.mouseCoords = coords;
		requestUpdate()
	}

	const pendingUpdate = ref(false);
	function requestUpdate() {
		if (pendingUpdate.value) return;
		pendingUpdate.value = true;
		requestAnimationFrame(doUpdate);
	}

	// Convert a pixel radius around `coords` into a degree radius for the
	// spatial query at the current zoom/latitude.
	function pixelRadiusToDeg(coords: [number, number], pixels: number): number {
		const map = store.mapInstance!;
		const p = map.project(coords);
		const east = map.unproject([p.x + pixels, p.y]);
		const south = map.unproject([p.x, p.y + pixels]);
		const dLng = Math.abs(east.lng - coords[0]);
		const dLat = Math.abs(south.lat - coords[1]);
		return Math.max(dLng, dLat);
	}

	function selectHoverMatch(matches: HoverMatch[]): LineProjection {
		const closestDistance = Math.min(...matches.map(({ distance }) => distance));
		const tiedMatches = matches.filter(({ distance }) => distance <= closestDistance + HOVER_TIE_TOLERANCE_PX);

		if (progressMode.value != "" && progressMode.value != "map" && store.actualTrackLength > 0) {
			const currentLocation = progress.value * store.actualTrackLength;
			return tiedMatches.sort((a, b) => {
				const progressDelta = Math.abs(a.match.location - currentLocation) - Math.abs(b.match.location - currentLocation);
				if (progressDelta != 0) return progressDelta;
				return a.match.location - b.match.location;
			})[0].match;
		}

		return tiedMatches.sort((a, b) => a.match.location - b.match.location)[0].match;
	}

	function selectFeatureProgress(progresses: number[]): number | undefined {
		if (progresses.length === 0) return undefined;
		if (progressMode.value != "" && progressMode.value != "map") {
			return [...progresses].sort((a, b) => Math.abs(a - progress.value) - Math.abs(b - progress.value))[0];
		}

		return progresses[0];
	}

	function doUpdate() {
		pendingUpdate.value = false;
		if (mouseState.featureId) {
			const featureProgress = selectFeatureProgress(mouseState.featureProgresses);
			if (featureProgress !== undefined) {
				setProgress(featureProgress, "marker", mouseState.featureId);
			}
		} else if (mouseState.mouseCoords) {
			const coords = mouseState.mouseCoords;
			// Search a little wider than the 30px hit threshold so the true nearest
			// segment is never pruned out before the exact pixel check below.
			const radiusDeg = pixelRadiusToDeg(coords, 40);
			const matches = store.trackIndex
				.nearbyDegreeWindow(coords, radiusDeg)
				.map(match => ({
					match,
					distance: pixelDistance(match.coordinates as LngLatLike, coords, store.mapInstance!),
				}))
				.filter(({ distance }) => distance < HOVER_HIT_RADIUS_PX);

			if (matches.length > 0) {
				const selected = selectHoverMatch(matches);
				setProgress(selected.location / store.actualTrackLength, "track");
			}
			else {
				setProgress(1, "map");
			}
		}
		mouseState.featureId = undefined;
		mouseState.mouseCoords = undefined;
		mouseState.featureProgresses = [];
	}

	return {
		progress,
		progressMode,
		progressFeature,

		animate,
		stopAnimation,
		setProgress,

		handleMouseMoveFeature,
		handleMouseMoveMap,
	}
})